import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { recipeV2Client } from '../../../app/services/api/recipeV2';
import { RecipeV2Detail } from '../types';
import { RecipePublishStyle, saveRecipePdf } from '../publishing/recipePublishing';

interface RecipePublishDialogProps {
  open: boolean;
  recipe: RecipeV2Detail;
  onClose: () => void;
}

const RecipePublishDialog = ({ open, recipe, onClose }: RecipePublishDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const [style, setStyle] = useState<RecipePublishStyle>('print');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const governedRecipe = await recipeV2Client.getRecipePublishingDetail(recipe.id);
      await saveRecipePdf(governedRecipe, { style });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.error', 'Unable to generate the PDF.'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open}
      disableRestoreFocus
      onClose={isGenerating ? undefined : onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>{t('publishing.recipeTitle', 'Export recipe')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography color="text.secondary">
            {t('publishing.recipeDescription', 'Generate a governed PDF from the currently published recipe version. Costing and other commercial information are excluded.')}
          </Typography>

          <RadioGroup
            value={style}
            onChange={(event) => setStyle(event.target.value as RecipePublishStyle)}
          >
            <FormControlLabel
              value="print"
              control={<Radio />}
              label={t('publishing.printFriendly', 'Print-friendly - minimal ink, no decorative imagery')}
            />
            <FormControlLabel
              value="full"
              control={<Radio />}
              label={t('publishing.fullColour', 'Full-colour - richer presentation with imagery where available')}
            />
          </RadioGroup>

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}
          disabled={isGenerating}>
          {t('actions.cancel', 'Cancel')}
        </Button>
        <Button
          variant="primary"
          startIcon={<PictureAsPdf />}
          disabled={isGenerating}
          onClick={() => { void handleGenerate(); }}
        >
          {isGenerating
            ? t('publishing.generating', 'Generating...')
            : t('publishing.downloadPdf', 'Download PDF')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipePublishDialog;
